import {
	Table, Row, Col, Button, Select, Badge, Tooltip, message, Spin,
	Form,
	Modal,
	DatePicker,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useBilling } from "context/BillingContext";
import moment from "moment";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { ReactComponent as Editicon } from "../../assets/Edit_Pencil_01.svg";
import { ReactComponent as TrashDelete } from "../../assets/Trash_Full.svg";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { ViewClaims } from "pages/Engagement/Billing/ViewClaims";
import { ViewTimelogs } from "pages/Matter/ViewTimelogs";

const markaspaidsendbtn = {
	background: "#deefee",
	border: "none",
	borderRadius: 2,
	color: "#000",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontWeight: 500,
	width: "100px",
};

const StyledTable = styled(Table)`
  padding-top: 10px;
  table {
    width: 100% !important;
    border: 1px solid #ccc;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    color: #333;
    font-size: 14px;
  }
  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: #333;
    white-space: nowrap;
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-table-tbody .ant-table-cell {
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-table-tbody > tr:hover > td {
    background-color: #fafafa !important;
  }
  .ant-select-selector {
    min-width: 150px;
  }
  .ant-table-tbody > tr .action-icons {
    display: flex;
    gap: 8px;
    align-items: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  .ant-table-tbody > tr:hover .action-icons {
    opacity: 1;
  }
  .btn-color{
	color: #00685F !important;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 12px; }
`;

const CancelBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 22px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #333;
  transition: all 0.15s;
  &:hover { background: #f5f5f5; }
`;

const Yesbtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 22px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  background: #c2fae5;
  color: #131111;
  transition: all 0.15s;
  &:hover { background: #a8f0d0; }
`;

// Claims & Timelog filter options
const claimsTimelogOptions = [
	{ label: "Has Claims", value: "hasClaims" },
	{ label: "Has Timelog", value: "hasTimelog" },
	{ label: "No Claims", value: "noClaims" },
	{ label: "No Timelog", value: "noTimelog" },
];

const Billing = ({ clientId, clientData }) => {
	const history = useHistory();
	const [hoveredRow, setHoveredRow] = useState(null);
	const [tableData, setTableData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [viewClaimsRecord, setViewClaimsRecord] = useState(null);
	const [viewTimelogRecord, setViewTimelogRecord] = useState(null); // ✅ Added missing state
	const [deleteModal, setDeleteModal] = useState(false);
	const [pendingDeleteRecord, setPendingDeleteRecord] = useState(null);
	const [sendLoadingId, setSendLoadingId] = useState(null);

	const [form] = Form.useForm();
	const { removeBill } = useBilling();

	const bdoId = clientData?.bdo_id;

	const mapBills = (raw) => {
		const claimsArr = Array.isArray(raw?.claims) ? raw.claims : [];
		const timelogArr = Array.isArray(raw?.timelog) ? raw.timelog : [];

		let status;
		if (raw?.paidstatus === "1" || raw?.paidstatus === true) {
			status = "paid";
		} else {
			status = (raw?.statusname ?? raw?.status ?? "pending").toLowerCase();
		}

		const flatTimelogs = timelogArr.map((t) => t?.timelogId ?? t);

		return {
			key: raw?.billingid,
			submittedBy: raw?.userid || raw?.createdby || "—",
			date: raw?.billingdate ? moment(raw.billingdate).format("Do MMM, YYYY") : "—",
			billingdate: raw?.billingdate ? moment(raw.billingdate) : null,
			billId: raw?.billingid || "—",
			taxType: raw?.vat || "Non-VAT",
			amount: raw?.amount != null ? `${raw?.currency ? raw.currency + " " : ""}${Number(raw.amount).toFixed(2)}` : "—",
			currency: raw?.currency || "",
			description: raw?.description || "",
			claims: claimsArr,
			claimsRaw: claimsArr,
			timeLog: flatTimelogs.length > 0
				? `${flatTimelogs.length} ${flatTimelogs.length === 1 ? "Log" : "Logs"}`
				: null,
			timelogRaw: flatTimelogs,
			status,
			actionBy: raw?.leadattorney || raw?.billingapprovedby || raw?.approvedby || "",
			_raw: raw,
		};
	};

	const fetchBills = useCallback(async (filters = {}) => {
		if (!bdoId) return;
		setLoading(true);
		try {
			const params = new URLSearchParams({ clientcode: bdoId });

			if (filters.submittedBy?.length)
				filters.submittedBy.forEach((v) => params.append("submittedby", v));
			if (filters.status?.length)
				filters.status.forEach((v) => params.append("status", v));
			if (filters.taxType?.length)
				filters.taxType.forEach((v) => params.append("taxtype", v));
			if (filters.claimsTimelog?.length)
				filters.claimsTimelog.forEach((v) => params.append("claimstimelog", v));
			if (filters.submittedFrom)
				params.append("submittedfrom", filters.submittedFrom.format("YYYY-MM-DD"));
			if (filters.submittedTo)
				params.append("submittedto", filters.submittedTo.format("YYYY-MM-DD"));

			const res = await CALL_API(`insyts/billing?${params.toString()}`, "get");

			const raw = res?.items ?? [];
			const mapped = (Array.isArray(raw) ? raw : []).map(mapBills);

			setTableData(mapped);
		} catch (err) {
			console.error("Billing fetch error:", err);
			message.error("Failed to load bills.");
		} finally {
			setLoading(false);
		}
	}, [bdoId]);

	useEffect(() => {
		fetchBills({});
	}, [fetchBills]);

	const handleFilterChange = () => {
		const filters = {
			submittedBy: form.getFieldValue("submittedBy") || [],
			status: form.getFieldValue("status") || [],
			taxType: form.getFieldValue("taxType") || [],
			claimsTimelog: form.getFieldValue("claimsTimelog") || [],
			submittedFrom: form.getFieldValue("submittedFrom") || null,
			submittedTo: form.getFieldValue("submittedTo") || null,
		};
		fetchBills(filters);
	};

	const handleDeleteClick = (record) => {
		setPendingDeleteRecord(record);
		setDeleteModal(true);
	};

	const handleDeleteCancel = () => {
		setDeleteModal(false);
		setPendingDeleteRecord(null);
	};

	const handleDeleteConfirm = async () => {
		try {
			await CALL_API(
				`insyts/billing?matterid=${pendingDeleteRecord._raw.matterid}&billingid=${pendingDeleteRecord.billId}`,
				"delete"
			);
			removeBill(pendingDeleteRecord.billId);
			setTableData((prev) => prev.filter((r) => r.key !== pendingDeleteRecord.key));
			message.success("Deleted successfully");
		} catch {
			message.error("Delete failed");
		} finally {
			handleDeleteCancel();
		}
	};

	const handleViewMatter = async (record, action) => {
		try {
			if (action === "send") setSendLoadingId(record.billId);
			const resp = await CALL_FEATCH_API(
				`insyts-matter/viewmatter?matterid=${record._raw.matterid}`,
				"GET"
			);
			const matterData =
				resp?.response?.data || resp?.response || resp?.data || resp;

			if (action === "markaspaid") {
				history.push({
					pathname: `/client-details/${record.matterid}/markaspaid`,
					state: {
						matterData,
						amount: {
							billId: record?.billId,
							amount: record?.amount,
							requestamount: record?.requestamount,
							currency: record?.currency,
							clientid: record?._raw?.clientcode,
							matterid: record?._raw?.matterid,
							fundid: record?.fundid,
						},
					},
				});
			} else if (action === "send") {
				history.push({
					pathname: `/matter-details/${record._raw.matterid}/billingemail`,
					state: { matterData },
				});
			}
		} catch (err) {
			console.error("viewmatter error:", err);
			message.error("Failed to load matter details.");
		} finally {
			setSendLoadingId(null);
		}
	};

	const submittedByOptions = [...new Set(tableData.map((r) => r.submittedBy).filter(Boolean))]
		.map((v) => ({ label: v, value: v }));

	const statusOptions = [...new Set(tableData.map((r) => r.status).filter(Boolean))]
		.map((v) => ({ label: v, value: v }));

	const taxTypeOptions = [...new Set(tableData.map((r) => r.taxType).filter(Boolean))]
		.map((v) => ({ label: v, value: v }));

	const columns = [
		{
			title: "Submitted by",
			dataIndex: "submittedBy",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<small style={{ color: "#777" }}>{record.date}</small>
				</div>
			),
		},
		{
			title: "Bill ID",
			dataIndex: "billId",
		},
		{
			title: "Tax Type",
			dataIndex: "taxType",
		},
		{
			title: "Amount",
			dataIndex: "amount",
		},
		{
			title: "Claims",
			dataIndex: "claims",
			render: (val, record) =>
				val?.length ? (
					<span
						style={{
							display: "inline-flex", alignItems: "center", color: "#1677ff",
							padding: "2px 10px", fontSize: 15, fontWeight: 500, cursor: "pointer"
						}}
						onClick={() => setViewClaimsRecord(record)}
					>
						{val.length} claims
					</span>
				) : <span style={{ color: "#bbb" }}>—</span>,
		},
		{
			title: "TimeLog",
			dataIndex: "timeLog",
			// ✅ Fixed: added `record` param and onClick handler
			render: (val, record) =>
				val ? (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							background: "#f0f9ff",
							color: "#0ea5e9",
							borderRadius: 20,
							padding: "2px 10px",
							fontSize: 12,
							fontWeight: 500,
							cursor: "pointer",
						}}
						onClick={() =>
							setViewTimelogRecord(record)}
					>
						{val}
					</span>
				) : <span style={{ color: "#bbb" }}>—</span>,
		},
		{
			title: "Status",
			dataIndex: "status",
			render: (_, record) => {
				const s = record.status;
				if (s === "paid")
					return <Badge status="success" text="Paid" />;
				if (s === "approved")
					return (
						<div>
							<div>Approved by: {record.actionBy}</div>
							<small style={{ color: "#777" }}>{record.date}</small>
						</div>
					);
				if (s === "declined")
					return (
						<div>
							<div>Declined by: {record.actionBy}</div>
							<small style={{ color: "#777" }}>{record.date}</small>
						</div>
					);
				return <span style={{ color: "#888" }}>Waiting for Approval</span>;
			},
		},
		{
			title: "",
			key: "actions",
			width: 280,
			render: (_, record) => {
				const status = record.status;
				return (
					<div className="action-icons">

						{/* ── Pending → Edit + Delete ── */}
						{status === "pending" && (
							<>
								<Editicon
									onClick={async () => {
										try {
											const resp = await CALL_FEATCH_API(
												`insyts-matter/viewmatter?matterid=${record._raw.matterid}`,
												"GET"
											);
											const matterData = resp?.response?.data || resp?.response || resp?.data || resp;
											history.push({
												pathname: `${history.location.pathname}/addbills`,
												state: { record: record._raw, matterid: record._raw.matterid, matterData, source: "Client" },
											});
										} catch (err) {
											console.error("viewmatter error:", err);
											message.error("Failed to load matter details.");
										}
									}}
									style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
								/>
								<TrashDelete
									onClick={() => handleDeleteClick(record)}
									style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
								/>
							</>
						)}

						{/* ── Waiting for Approval → Edit + Delete ── */}
						{status === "waiting for approval" && (
							<>
								<Editicon
									onClick={async () => {
										try {
											const resp = await CALL_FEATCH_API(
												`insyts-matter/viewmatter?matterid=${record._raw.matterid}`,
												"GET"
											);
											const matterData = resp?.response?.data || resp?.response || resp?.data || resp;
											history.push({
												pathname: `${history.location.pathname}/addbills`,
												state: { record: record._raw, matterid: record._raw.matterid, matterData, source: "Client" },
											});
										} catch (err) {
											console.error("viewmatter error:", err);
											message.error("Failed to load matter details.");
										}
									}}
									style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
								/>
								<TrashDelete
									onClick={() => handleDeleteClick(record)} // ✅ Fixed: was passing record._raw, now passes record
									style={{ cursor: "pointer", backgroundColor: "#C2FAE5" }}
								/>
							</>
						)}

						{/* ── Approved → Mark as Paid + Send ── */}
						{status === "approved" && (
							<>
								<Button
									className="btn-color"
									size="small"
									style={markaspaidsendbtn}
									onClick={() => handleViewMatter(record, "markaspaid")}
								>
									Mark as Paid
								</Button>
								<Button
									className="btn-color"
									size="small"
									style={markaspaidsendbtn}
									loading={sendLoadingId === record.billId}
									onClick={() => handleViewMatter(record, "send")}
								>
									Send
								</Button>
							</>
						)}

						{/* ── Declined → View only ── */}
						{status === "declined" && (
							<EyeOutlined
								onClick={() => history.push({
									pathname: `${history.location.pathname}/addbills`,
									state: { record: record._raw, viewbillings: true, bdoId, clientData },
								})}
								style={{ cursor: "pointer", background: "#C2FAE5", padding: 6, borderRadius: 4 }}
							/>
						)}

						{/* ── Paid ── */}
						{status === "paid" && (
							<span style={{
								display: "inline-flex", alignItems: "center", gap: 6,
								border: "1.5px solid #52c41a", borderRadius: 20,
								padding: "2px 12px", fontSize: 13,
								color: "#52c41a", background: "transparent", fontWeight: 500,
							}}>
								<span style={{
									width: 8, height: 8, borderRadius: "50%",
									border: "2px solid #52c41a", display: "inline-block",
								}} />
								Paid
							</span>
						)}

					</div>
				);
			},
		},
	];

	return (
		<div style={{ padding: "28px 0" }}>
			{/* Header */}
			<Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
				<Col><b>Total Bills: {tableData?.length}</b></Col>
				<Col>
					<Form
						form={form}
						layout="vertical"
						onValuesChange={handleFilterChange}
					>
						<div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>

							{/* Submitted By */}
							<Form.Item name="submittedBy" style={{ marginBottom: 0 }}>
								<DropdownCheckBox
									name="submittedBy"
									label="Submitted by"
									options={submittedByOptions}
									form={form}
									handleFormChange={handleFilterChange}
								/>
							</Form.Item>

							{/* Status */}
							<Form.Item name="status" style={{ marginBottom: 0 }}>
								<DropdownCheckBox
									name="status"
									label="Status"
									options={statusOptions}
									form={form}
									handleFormChange={handleFilterChange}
								/>
							</Form.Item>

							{/* Tax Type */}
							<Form.Item name="taxType" style={{ marginBottom: 0 }}>
								<DropdownCheckBox
									name="taxType"
									label="Tax Type"
									options={taxTypeOptions}
									form={form}
									handleFormChange={handleFilterChange}
								/>
							</Form.Item>

							{/* Claims & Timelog */}
							<Form.Item name="claimsTimelog" style={{ marginBottom: 0 }}>
								<DropdownCheckBox
									name="claimsTimelog"
									label="Claims & Timelog"
									options={claimsTimelogOptions}
									form={form}
									handleFormChange={handleFilterChange}
								/>
							</Form.Item>

							{/* Submitted From */}
							<Form.Item name="submittedFrom" style={{ marginBottom: 16 }}>
								<DatePicker
									placeholder="Submitted From"
									format="DD MMM YYYY"
									onChange={handleFilterChange}
									style={{ borderRadius: 20, height: 32 }}
								/>
							</Form.Item>

							{/* Arrow separator */}
							<span style={{ color: "#888", fontSize: 16, marginBottom: 16 }}>↔</span>

							{/* Submitted To */}
							<Form.Item name="submittedTo" style={{ marginBottom: 16 }}>
								<DatePicker
									placeholder="Submitted To"
									format="DD MMM YYYY"
									onChange={handleFilterChange}
									style={{ borderRadius: 20, height: 32 }}
								/>
							</Form.Item>
						</div>
					</Form>
				</Col>
			</Row>

			{/* Table */}
			<Spin spinning={loading}>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={false}
					onRow={(record) => ({
						onMouseEnter: () => setHoveredRow(record.key),
						onMouseLeave: () => setHoveredRow(null),
					})}
				/>
			</Spin>

			{/* ── View Claims Modal ── */}
			<ViewClaims
				isModalVisible={!!viewClaimsRecord}
				setIsModalVisible={() => setViewClaimsRecord(null)}
				addClaims={viewClaimsRecord?.claimsRaw || []}
				setaddClaims={() => { }}
				claimsDataSource={viewClaimsRecord?.claimsRaw || []}
				isViewClims={true}
				viewClims={true}
				update={false}
				activeclaimsIds={[]}
			/>

			{/* ── View Timelogs Modal ── ✅ Added missing component */}
			<ViewTimelogs
				isModalVisible={!!viewTimelogRecord}
				setIsModalVisible={() => setViewTimelogRecord(null)}
				addTimelogs={viewTimelogRecord?.timelogRaw || []}
				setAddTimelogs={() => { }}
				timelogsDataSource={viewTimelogRecord?.timelogRaw || []}  // ✅ pass directly, no mapping
				isViewTimelogs={true}
				viewTimelogs={true}
				update={false}
				activeTimelogIds={[]}
				matterid={null}
			/>

			{/* ── Delete Confirmation Modal ── */}
			<StyledModal
				open={deleteModal}
				onCancel={handleDeleteCancel}
				footer={null}
				width={500}
				closable={false}
				centered
				destroyOnClose
			>
				<div style={{ textAlign: "left", padding: "24px 16px 8px" }}>
					<p style={{ fontSize: 15, fontWeight: 500, color: "#111827", marginBottom: 32 }}>
						Are you sure you want to delete this Bill?
					</p>
					<div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
						<CancelBtn onClick={handleDeleteCancel} style={{ width: 150, borderRadius: 10 }}>
							No
						</CancelBtn>
						<Yesbtn onClick={handleDeleteConfirm} style={{ width: 150 }}>
							Yes
						</Yesbtn>
					</div>
				</div>
			</StyledModal>
		</div>
	);
};

export default Billing;